import { useCallback, useRef, useState } from 'react';
import { Download, FileUp } from 'lucide-react';

import { ApiError } from '@/api/client';
import type { VacancyImportPreview, VacancyImportResult, VacancyImportRow } from '@/api/schema';
import { Alert } from '@/components/feedback/Alert';
import { useToast } from '@/components/feedback/Toast';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { Modal } from '@/components/ui/Modal';
import { Table, TableWrap, Td, Th, Tr } from '@/components/ui/Table';
import { apiUrl } from '@/lib/env';
import { useVacancyImportCommit, useVacancyImportPreview } from '../api';
import styles from '@/features/candidates/components/ImportModal.module.css';

const STATE_LABEL = { draft: 'Draft', published: 'Published', closed: 'Closed' };

export function VacancyImportModal({ onClose }: { onClose: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  const busy = useRef(false);
  const toast = useToast();
  const preview = useVacancyImportPreview();
  const commit = useVacancyImportCommit();
  const [checked, setChecked] = useState<{ file: File; report: VacancyImportPreview } | null>(
    null,
  );
  const [result, setResult] = useState<VacancyImportResult | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const pending = preview.isPending || commit.isPending;
  const close = useCallback(() => {
    if (!busy.current) onClose();
  }, [onClose]);

  const choose = async (file: File) => {
    if (busy.current) return;
    busy.current = true;
    setChecked(null);
    setResult(null);
    setProblem(null);
    try {
      const report = await preview.mutateAsync(file);
      setChecked({ file, report });
    } catch (error) {
      setProblem(
        error instanceof ApiError ? error.message : 'The file could not be checked. Try again.',
      );
    } finally {
      busy.current = false;
    }
  };

  const importRows = async () => {
    if (busy.current || !checked || checked.report.valid_count === 0 || result) return;
    busy.current = true;
    setProblem(null);
    try {
      const outcome = await commit.mutateAsync(checked.file);
      setResult(outcome);
      toast.success(
        `${outcome.created} ${outcome.created === 1 ? 'vacancy' : 'vacancies'} imported.`,
      );
    } catch (error) {
      setProblem(
        error instanceof ApiError && error.status >= 400 && error.status < 500
          ? error.message
          : 'The import outcome could not be confirmed. Check Careers before retrying.',
      );
    } finally {
      busy.current = false;
    }
  };

  return (
    <Modal
      open
      title="Import vacancies"
      description="Upload a CSV to create multiple job listings. Published vacancies appear on the public Jobs page."
      size="lg"
      icon={<FileUp size={19} />}
      onClose={close}
      footer={
        result ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close} disabled={pending}>
              Cancel
            </Button>
            <Button
              disabled={!checked || checked.report.valid_count === 0 || pending}
              loading={commit.isPending}
              onClick={() => {
                void importRows();
              }}
            >
              Import {checked?.report.valid_count ?? 0}{' '}
              {checked?.report.valid_count === 1 ? 'vacancy' : 'vacancies'}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className={styles.preview}>
          <Alert tone="success">
            <b>
              {result.created} {result.created === 1 ? 'vacancy' : 'vacancies'} created.
            </b>{' '}
            {result.published_count} published, {result.draft_count} drafts,{' '}
            {result.closed_count} closed.
          </Alert>
          {result.skipped > 0 ? (
            <Alert tone="warning">
              {result.skipped} rows were skipped. Correct the rows listed below before importing
              them again.
            </Alert>
          ) : null}
          {result.skipped_rows.length > 0 ? <Rows rows={result.skipped_rows} /> : null}
        </div>
      ) : (
        <>
          <div className={styles.templateRow}>
            <span className={styles.templateText}>
              Replace the example rows with your vacancies. Save as CSV UTF-8 from Excel or
              Google Sheets.
            </span>
            <a
              className={styles.templateLink}
              href={apiUrl('/api/v1/careers/vacancies/import/template')}
              download
            >
              <Download size={14} aria-hidden="true" /> Download the template
            </a>
          </div>
          <div
            className={styles.zone}
            onDragOver={(event) => {
              event.preventDefault();
            }}
            onDrop={(event) => {
              event.preventDefault();
              const file = event.dataTransfer.files[0];
              if (file) void choose(file);
            }}
          >
            <FileUp size={22} aria-hidden="true" />
            <p className={styles.zoneTitle}>
              {checked?.file.name ?? 'Drop a CSV here, or choose a file'}
            </p>
            <p className={styles.zoneHint}>Up to 5 MB and 1,000 vacancies per file.</p>
            <Button
              variant="ghost"
              disabled={pending}
              onClick={() => {
                input.current?.click();
              }}
            >
              Choose a file
            </Button>
            <input
              aria-label="Vacancies CSV file"
              ref={input}
              className={styles.input}
              type="file"
              accept=".csv,text/csv"
              disabled={pending}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) void choose(file);
              }}
            />
          </div>
          <p className={styles.note}>
            Required: Job title, Industry, Country and Description. Status accepts Published,
            Draft or Closed; a blank status creates a draft. Use Yes or No in Highlighted to
            choose jobs for the public spotlight.
          </p>
          {preview.isPending ? <p role="status">Checking the file...</p> : null}
          {commit.isPending ? <p role="status">Importing vacancies...</p> : null}
          {checked ? (
            <div className={styles.preview}>
              <Alert tone={checked.report.invalid_count ? 'warning' : 'success'}>
                <b>
                  {checked.report.valid_count}{' '}
                  {checked.report.valid_count === 1 ? 'row' : 'rows'} ready to import.
                </b>{' '}
                {checked.report.invalid_count} rows will be skipped.
              </Alert>
              <Alert tone="info">
                {checked.report.published_count} will appear on the public Jobs page.{' '}
                {checked.report.draft_count} will remain drafts and{' '}
                {checked.report.closed_count} will be closed. This creates new vacancies; it
                does not update existing jobs.
              </Alert>
              <Rows rows={checked.report.rows} />
            </div>
          ) : null}
        </>
      )}
      {problem ? <Alert tone="danger">{problem}</Alert> : null}
    </Modal>
  );
}

function Rows({ rows }: { rows: VacancyImportRow[] }) {
  return (
    <TableWrap>
      <Table minWidth={620}>
        <thead>
          <Tr>
            <Th label="Line" />
            <Th label="Job title" />
            <Th label="Country" />
            <Th label="Highlighted" />
            <Th label="Status" />
            <Th label="Issues" />
          </Tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <Tr key={row.line} className={row.valid ? undefined : styles.rowInvalid}>
              <Td>{row.line}</Td>
              <Td>{row.title || 'Missing title'}</Td>
              <Td>{row.country}</Td>
              <Td>{row.highlighted == null ? '' : row.highlighted ? 'Yes' : 'No'}</Td>
              <Td>
                <Chip tone={row.valid ? 'approved' : 'rejected'} size="sm">
                  {row.valid && row.state ? STATE_LABEL[row.state] : 'Skipped'}
                </Chip>
              </Td>
              <Td>
                {row.errors.map((error, index) => (
                  <p key={`${error.column}-${index}`}>
                    <b>{error.column}</b>: {error.message}
                  </p>
                ))}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </TableWrap>
  );
}
